// FTC (16 CFR Part 255) and eBay Partner Network compliant affiliate
// disclosure. EPN requires a clear, conspicuous statement that outbound
// merchant links can earn the site a commission, and the FTC requires it to sit
// next to the affiliate links themselves rather than only in a policy page — so
// this component is rendered on every page that carries marketplace links.

import { useQuery } from "@tanstack/react-query";
import { getPriceSyncTime } from "@/lib/prices.functions";

/** Formats an ISO timestamp as a fixed UTC string (SSR-safe, no locale drift). */
export function formatUtcStamp(iso: string | null | undefined): string {
  if (!iso) return "not yet synced";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "not yet synced";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

export interface AffiliateDisclosureProps {
  /** ISO timestamp the displayed prices were last refreshed. */
  updated?: string | null;
  /** Compact single-line variant for footers and card panels. */
  compact?: boolean;
  className?: string;
}

/**
 * When a page does not already hold a price board, the disclosure resolves the
 * real last sync itself so the stamp is identical site-wide and never reads
 * "not yet synced" on card pages.
 */
function useSyncedStamp(updated?: string | null): string | null | undefined {
  const { data } = useQuery({
    queryKey: ["price-sync-time"],
    queryFn: () => getPriceSyncTime(),
    staleTime: 15 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    enabled: updated === undefined || updated === null,
  });
  return updated ?? data?.updated ?? null;
}

/**
 * English-only data note. Every buy / sold link and every tracked price on the
 * site samples ENGLISH printings, so the disclosure states it wherever prices
 * lead to a marketplace. Japanese quotes, where tracked, are shown as their own
 * clearly labelled series.
 */
const ENGLISH_ONLY =
  "Card prices track English printings only; Japanese printings are shown separately where tracked.";

const DISCLOSURE =
  "When you click on links to various merchants on this site and make a purchase, this can result in this site earning a commission. Affiliate programs and affiliations include, but are not limited to, TCGPlayer, & the eBay Partner Network.";

export function AffiliateDisclosure({ updated, compact = false, className = "" }: AffiliateDisclosureProps) {
  const stamp = formatUtcStamp(useSyncedStamp(updated));
  if (compact) {
    return (
      <p className={`text-xs text-muted-foreground ${className}`} suppressHydrationWarning>
        <strong className="text-foreground">Affiliate disclosure:</strong> {DISCLOSURE} {ENGLISH_ONLY} Prices updated as of {stamp}.
      </p>
    );
  }
  return (
    <aside
      aria-label="Affiliate disclosure"
      className={`glass rounded-xl border border-border/60 p-4 text-xs leading-relaxed text-muted-foreground ${className}`}
    >
      <p className="font-semibold uppercase tracking-widest text-primary">Affiliate disclosure</p>
      <p className="mt-2 max-w-4xl">{DISCLOSURE}</p>
      <p className="mt-2 max-w-4xl">{ENGLISH_ONLY}</p>
      <p className="mt-2">
        Prices updated as of{" "}
        <strong className="text-foreground tabular-nums" suppressHydrationWarning>
          {stamp}
        </strong>
        .
      </p>
    </aside>
  );
}
